/**
 * 同期コンテキスト
 * 
 * エディタとチャット間の同期状態を管理するコンテキスト
 * 依存性注入により、テスタビリティとモジュール性を向上
 */

import React, { createContext, useContext } from 'react';
import { proxy } from 'valtio';

import { syncStore as defaultSyncStore } from '@/services/editor-chat-sync';

import type { SyncState } from '@/services/editor-chat-sync';
import type { ReactNode } from 'react';

interface SyncContextValue {
  store: typeof defaultSyncStore;
}

const SyncContext = createContext<SyncContextValue | null>(null);

interface SyncProviderProps {
  children: ReactNode;
  // テスト時にカスタムストアを注入できるようにする
  store?: typeof defaultSyncStore;
}

/**
 * 同期プロバイダー
 * アプリケーションのルートで使用し、同期ストアを提供
 */
export function SyncProvider({ 
  children, 
  store = defaultSyncStore 
}: SyncProviderProps) {
  return (
    <SyncContext.Provider value={{ store }}>
      {children}
    </SyncContext.Provider>
  );
}

/**
 * 同期コンテキストを使用するフック
 * @throws {Error} SyncProvider外で使用された場合
 */
export function useSync(): SyncContextValue {
  const context = useContext(SyncContext);
  if (!context) {
    throw new Error('useSync must be used within a SyncProvider');
  }
  return context;
}

/**
 * テスト用のモックストアを作成するヘルパー
 */
export function createMockSyncStore(initialState?: Partial<SyncState>): typeof defaultSyncStore {
  return proxy<SyncState>({
    editorState: 'uninitialized',
    editorBlocks: [],
    chatState: 'idle',
    isAIGenerating: false,
    pendingEditorUpdates: [],
    pendingSuggestionPreview: null,
    pendingSuggestionPreviews: [],
    resolvedProposals: {},
    pendingOperationToggle: null,
    ...initialState,
  }) as typeof defaultSyncStore;
}
